import React from 'react';
import { CrudPage } from '../../components/CrudPage';
import { type MaintenanceWindowDto, MaintenanceWindowControllerService } from '../../api';
import MaintenanceWindowFormDialog from '../forms/MaintenanceWindowFormDialog';
import { useTranslation } from 'react-i18next';

// Type with required id for the CrudPage
type MaintenanceWindowWithId = MaintenanceWindowDto & { id: string };

// Transform function to add string id from uuid
const transformMaintenanceWindow = (item: MaintenanceWindowDto): MaintenanceWindowWithId => ({
  ...item,
  id: item.uuid || ''
} as MaintenanceWindowWithId);

const MaintenanceWindowsPage: React.FC = () => {
  const { i18n } = useTranslation();

  // Custom renderer for datetime with locale formatting
  const dateTimeRenderer = (data: MaintenanceWindowWithId, field: string) => {
    const value = data[field as keyof MaintenanceWindowWithId];
    if (typeof value !== 'string' || !value) return '';
    
    try {
      const date = new Date(value);
      return new Intl.DateTimeFormat(i18n.language === 'de' ? 'de-DE' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch {
      return value;
    }
  };

  // Helper function to convert MaintenanceWindowDto[] to MaintenanceWindowWithId[]
  const fetchData = async (): Promise<MaintenanceWindowWithId[]> => {
    try {
      const maintenanceWindows = await MaintenanceWindowControllerService.findAll();
      return maintenanceWindows.map(transformMaintenanceWindow);
    } catch (error) {
      console.error('Error fetching maintenance windows:', error);
      return [];
    }
  };

  const saveData = async (data: MaintenanceWindowWithId): Promise<void> => {
    try {
      const maintenanceWindowData: MaintenanceWindowDto = {
        ...data,
        uuid: data.id // id is the uuid in our internal type
      };
      
      if (data.uuid) {
        // Update existing maintenance window
        await MaintenanceWindowControllerService.update(data.uuid, maintenanceWindowData);
      } else {
        // Create new maintenance window
        await MaintenanceWindowControllerService.create(maintenanceWindowData);
      }
    } catch (error) {
      console.error('Error saving maintenance window:', error);
      throw error;
    }
  };

  const deleteData = async (uuid: string): Promise<void> => {
    try {
      await MaintenanceWindowControllerService.delete(uuid);
    } catch (error) {
      console.error('Error deleting maintenance window:', error);
      throw error;
    }
  };

  return (
    <CrudPage<MaintenanceWindowWithId>
      titleKey="headers.maintenanceWindows"
      gridMeta={[
        { field: 'maintenanceType', headerKey: 'maintenanceWindow.grid.field.maintenanceType', width: 120 },
        { field: 'resourceName', headerKey: 'maintenanceWindow.grid.field.resource', width: 150 },
        { 
          field: 'maintenanceStart', 
          headerKey: 'maintenanceWindow.grid.field.startDate', 
          width: 150,
          render: (data: MaintenanceWindowWithId) => dateTimeRenderer(data, 'maintenanceStart')
        },
        { 
          field: 'maintenanceEnd', 
          headerKey: 'maintenanceWindow.grid.field.endDate', 
          width: 150,
          render: (data: MaintenanceWindowWithId) => dateTimeRenderer(data, 'maintenanceEnd')
        },
        { field: 'maintenanceDescription', headerKey: 'maintenanceWindow.grid.field.maintenanceDescription', width: 200 }
      ]}
      fetchData={fetchData}
      saveData={saveData}
      deleteData={deleteData}
      FormComponent={MaintenanceWindowFormDialog}
      entityName="maintenancewindow"
    />
  );
};

export default MaintenanceWindowsPage;
